/**
 * The stop-loss on the Flow Optimizer's rule card (Phase 28, same card as
 * the verdicts): "Halt auto-send for today when …", then its two guards —
 * the settled SMS it needs before it may halt, and how long a send settles,
 * as "Only when" rows inside the conditions (20/10/2026).
 * A deeper rule set through the API is shown as such and kept as it is.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Ban } from 'lucide-react';

import { HintDot } from '@/components/ui';

import { OnlyWhen } from '../../../editor/verdicts/card/only-when/OnlyWhen';
import { VerdictCard } from '../../../editor/verdicts/card/VerdictCard';
import type { AutoSendDraft } from './autosend-draft';

export const StopLossFields: FC<{
  draft: AutoSendDraft;
  onPatch: (patch: Partial<AutoSendDraft>) => void;
  /** A container's own rule is optional — its group's still judges it. */
  optionalFor?: string;
  /** Phase 35 — "From your history" under the outcome (the editor passes it). */
  history?: ReactNode;
  /** What it is judged on, behind the card's ⓘ. */
  hint?: string;
}> = ({ draft, onPatch, optionalFor, history, hint }) => (
  <div className="flex flex-col gap-3">
    <VerdictCard
      icon={Ban}
      lead={
        <>
          Halt auto-send <span className="text-destructive">for today</span>
        </>
      }
      aside={hint ? <HintDot label={hint} /> : undefined}
      rule={{ join: draft.join, rows: draft.rows, advanced: draft.advanced }}
      onPatch={onPatch}
      starter={{ metric: 'deliveredRate', op: '<', value: 0.85 }}
      emptyHint={optionalFor ? undefined : 'Optional — without one, only the daily cap and Kill stop the day.'}
      footer={
        <OnlyWhen sample={{ value: draft.minSample, onChange: (minSample) => onPatch({ minSample }), unit: 'sms' }} settle={{ value: draft.delayMin, onChange: (delayMin) => onPatch({ delayMin }) }} />
      }
      outcome={{ tone: 'destructive', icon: Ban, label: 'halt auto-send' }}
    >
      <p className="text-xs text-muted-foreground">nothing sends by itself for the rest of the day · tomorrow starts clean</p>
      {history}
    </VerdictCard>
  </div>
);
